/* eslint-disable jsx-a11y/tabindex-no-positive */
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import Loader from '~/ui/components/common/Loader';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';

import FormFooter from './components/FormFooter';
import PersonalDetailsHeader from './components/PersonalDetailsHeader';
import PersonalDetailsLeftColumn from './components/PersonalDetailsLeftColumn';
import PersonalDetailsRightColumn from './components/PersonalDetailsRightColumn';
import WorkDetailsSection from './components/WorkDetailsSection';
import { useGuTeamAssignment, useLocalAdminDictionaryOptions } from './hooks';

import { ILocalAdminForm } from '~/services/api/localAdmin/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import Features from '~/ui/constants/features';
import createValidationSchema, { editValidationSchema } from './validate';

import type { ILocalAdminFormProps } from './types';

import styles from './Form.module.scss';

const Form = ({
  defaultValues,
  isEdit = false,
  submitText = 'Send Invite',
  isProcessing,
  leavePageDescription,
  cancelLinkTo,
  emailDomainOptions,
  isPending,
  onSubmit,
}: ILocalAdminFormProps): ReactElement => {
  const showError = useStoreActions(actions => actions.snackbar.showError);
  const clinic = useStoreState(state => state.clinic.current);
  const { options, dictLoading } = useLocalAdminDictionaryOptions(showError);
  const { isProgramAssistant } = useRole();

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<ILocalAdminForm>({
    mode: 'onBlur',
    defaultValues,
    resolver: isEdit ? editValidationSchema : createValidationSchema,
  });

  const emailDomain = watch('emailDomain');
  const primaryRole = watch('primaryRole');

  const {
    isGlobalUserSelected,
    filteredMemberOptions,
    teamOptions,
    restrictGuTeamsForPa,
    setAll,
    isTeamOptionDisabledForPa,
    normalizeTeamIdsMultiValue,
    teamsFetching,
    showPrimaryRoleTeams,
  } = useGuTeamAssignment({
    defaultValues,
    primaryRole,
    setValue,
  });

  const { isSuperAdmin, isProductOwner } = useRole();
  const crisisFeatureEnabled = !!clinic.features?.includes(Features.CRISIS);

  useEffect(() => {
    if (emailDomainOptions?.length && !emailDomain) {
      setValue('emailDomain', String(emailDomainOptions[0].value));
    }
  }, [emailDomain, emailDomainOptions, setValue]);

  if (dictLoading || teamsFetching || !options) return <Loader />;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.formContainer}>
        <Paper>
          <Box sx={{ p: 2, pr: 0 }}>
            <PersonalDetailsHeader restrictGuTeamsForPa={restrictGuTeamsForPa} />
            <Grid container>
              <PersonalDetailsLeftColumn
                isEdit={isEdit}
                isPending={isPending ?? false}
                emailDomainOptions={emailDomainOptions}
                control={control}
                register={register}
                errors={errors}
              />
              <PersonalDetailsRightColumn
                isProgramAssistant={isProgramAssistant}
                restrictGuTeamsForPa={restrictGuTeamsForPa}
                showPrimaryRoleTeams={showPrimaryRoleTeams}
                isGlobalUserSelected={isGlobalUserSelected}
                filteredMemberOptions={filteredMemberOptions}
                teamOptions={teamOptions}
                setAll={setAll}
                isTeamOptionDisabledForPa={isTeamOptionDisabledForPa}
                normalizeTeamIdsMultiValue={normalizeTeamIdsMultiValue}
                options={options}
                control={control}
                register={register}
                errors={errors}
              />
            </Grid>
          </Box>
        </Paper>
        <div className={styles.paperContainer}>
          <WorkDetailsSection
            options={options}
            control={control}
            register={register}
            errors={errors}
            clinicHasCrisisTeam={!!clinic.hasCrisisTeam}
            showCrisisCheckbox={Boolean(isSuperAdmin || isProductOwner) && crisisFeatureEnabled}
          />
          <FormFooter
            cancelLinkTo={cancelLinkTo}
            isProcessing={isProcessing}
            submitText={submitText}
            isValid={Object.keys(errors).length === 0}
          />
        </div>
      </form>
      <NavigationConfirmModal when={isDirty && !isProcessing} description={leavePageDescription} />
    </div>
  );
};

export default Form;
